/**
 * clothPipeline — GPU cloth simulation using the Verlet integration +
 * distance-constraint pattern from Simon Green's "Stupid OpenGL Shader
 * Tricks" (GDC 2003).
 *
 * Three fragment-shader passes, ping-ponged across two float-texture
 * FBOs each frame:
 *   1. Integrate — Verlet step (pos += pos - prev_pos + accel * dt^2)
 *   2. Constrain — distance constraints between neighbors, plus
 *      floor and sphere collision.
 *   3. Render — read positions, draw a 96x96 mesh of triangles with
 *      vertex displacement and Blinn-Phong lighting.
 *
 * Two position FBOs (current / previous) are ping-ponged each frame.
 * All math is float32 — important for stability over many frames.
 *
 * Attribution: pattern from Simon Green, NVIDIA, GDC 2003 ("Stupid
 * OpenGL Shader Tricks"). My implementation, my colors, my wind.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface ClothPipeline {
  programs: {
    integrate: WebGLProgram;
    constrain: WebGLProgram;
    init: WebGLProgram;
    render: WebGLProgram;
  };
  vaos: {
    integrate: WebGLVertexArrayObject;
    constrain: WebGLVertexArrayObject;
    init: WebGLVertexArrayObject;
    render: WebGLVertexArrayObject;
  };
  vbos: {
    integrate: WebGLBuffer;
    constrain: WebGLBuffer;
    init: WebGLBuffer;
    render: WebGLBuffer;
    renderIndices: WebGLBuffer;
  };
  uniforms: {
    integrate: {
      xTex: WebGLUniformLocation | null;
      oxTex: WebGLUniformLocation | null;
      timestep: WebGLUniformLocation | null;
      damping: WebGLUniformLocation | null;
      wind: WebGLUniformLocation | null;
      gravity: WebGLUniformLocation | null;
      gridSize: WebGLUniformLocation | null;
    };
    constrain: {
      xTex: WebGLUniformLocation | null;
      gridSize: WebGLUniformLocation | null;
      restLen: WebGLUniformLocation | null;
      stiffness: WebGLUniformLocation | null;
      floor: WebGLUniformLocation | null;
      spherePos: WebGLUniformLocation | null;
      sphereRadius: WebGLUniformLocation | null;
    };
    init: {
      gridSize: WebGLUniformLocation | null;
    };
    render: {
      xTex: WebGLUniformLocation | null;
      time: WebGLUniformLocation | null;
      cameraPos: WebGLUniformLocation | null;
      light1Pos: WebGLUniformLocation | null;
      light1Color: WebGLUniformLocation | null;
      light2Pos: WebGLUniformLocation | null;
      light2Color: WebGLUniformLocation | null;
    };
  };
  fboA: WebGLFramebuffer;
  texA: WebGLTexture;
  fboB: WebGLFramebuffer;
  texB: WebGLTexture;
  /** Width/height of the cloth grid in particles (e.g. 96 for a 96x96 grid). */
  gridSize: number;
  /** Texture resolution (always gridSize+2 for halo of edge padding). */
  texRes: number;
  /** Current "current" FBO. Ping-pongs each frame. */
  fboCurrent: WebGLFramebuffer;
  texCurrent: WebGLTexture;
  fboPrev: WebGLFramebuffer;
  texPrev: WebGLTexture;
  /** Number of vertices in the render mesh (gridSize * gridSize). */
  vertexCount: number;
  /** Number of indices in the render mesh. */
  indexCount: number;
}

const GRID = 96;        // particles per side
const TEXRES = GRID + 2; // 1-pixel halo so neighbor lookups stay in-bounds

// === Vertex shader: fullscreen quad (used for the integrate, constrain,
// and init passes — they all run a fragment shader on a fullscreen quad).
const VERT_QUAD = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

// === Vertex shader: cloth mesh. Each vertex has a vUV that maps to a
// particle position in the position texture. We sample the texture in
// the vertex shader, transform to clip space, and pass the world position
// to the fragment shader for lighting.
const VERT_CLOTH = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aUV;
uniform sampler2D uX;
uniform float uGridSize;
uniform vec3 uCameraPos;
uniform mat4 uViewProj;

out vec3 vWorldPos;
out vec2 vUV;

void main() {
  vec3 worldPos = texture(uX, aUV).xyz;
  vWorldPos = worldPos;
  vUV = aUV;

  // Look-at: target is (0, 1.5, 0) (the cloth's center of mass).
  vec3 target = vec3(0.0, 1.5, 0.0);
  vec3 fwd = normalize(target - uCameraPos);
  vec3 right = normalize(cross(fwd, vec3(0.0, 1.0, 0.0)));
  vec3 up = cross(right, fwd);
  vec3 toVert = worldPos - uCameraPos;
  float depth = dot(toVert, fwd);
  // Skip if behind the camera.
  if (depth < 0.1) {
    gl_Position = vec4(0.0, 0.0, 1.0, 1.0);
    return;
  }
  // 60° FOV: focal = 1/tan(30°) ≈ 1.732.
  float focal = 1.732;
  vec2 ndc = vec2(
    dot(toVert, right) * focal / depth,
    dot(toVert, up) * focal / depth
  ) * 0.55;

  gl_Position = vec4(ndc, depth * 0.001, 1.0);
}
`;

// --- 1. Integrate pass: Verlet step. ---
const FRAG_INTEGRATE = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;
uniform sampler2D uX;       // current positions (RGB = xyz)
uniform sampler2D uOX;      // previous positions
uniform float uTimestep;
uniform float uDamping;
uniform vec3 uWind;
uniform vec3 uGravity;
uniform float uGridSize;

void main() {
  vec3 x = texture(uX, vUV).xyz;
  vec3 oldx = texture(uOX, vUV).xyz;

  // Pin the top row in place — these are the "hanging points".
  float row = vUV.y * (uGridSize - 1.0);
  if (row < 0.5) {
    outColor = vec4(0.0, 4.0, 0.0, 1.0);
    return;
  }

  vec3 accel = uGravity + uWind;
  vec3 newX = x + uDamping * (x - oldx) + accel * uTimestep * uTimestep;

  outColor = vec4(newX, 1.0);
}`;

// --- 2. Constrain pass: distance constraints to 4 neighbors + floor + sphere. ---
const FRAG_CONSTRAIN = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;
uniform sampler2D uX;
uniform float uGridSize;
uniform float uRestLen;
uniform float uStiffness;
uniform float uFloor;
uniform vec3 uSpherePos;
uniform float uSphereRadius;

void main() {
  float row = vUV.y * (uGridSize - 1.0);
  if (row < 0.5) {
    outColor = vec4(0.0, 4.0, 0.0, 1.0);
    return;
  }

  vec3 x = texture(uX, vUV).xyz;
  float texel = 1.0 / uGridSize;
  vec2 uv = vUV;

  vec3 dx = vec3(0.0);
  vec3 neighbor;
  vec3 delta;
  float d;

  // 4-neighbor constraints, each at quarter strength.
  neighbor = texture(uX, uv + vec2(texel, 0.0)).xyz;
  delta = neighbor - x; d = length(delta);
  dx += delta * (d - uRestLen) / max(d, 1e-5) * 0.25 * uStiffness;

  neighbor = texture(uX, uv - vec2(texel, 0.0)).xyz;
  delta = neighbor - x; d = length(delta);
  dx += delta * (d - uRestLen) / max(d, 1e-5) * 0.25 * uStiffness;

  neighbor = texture(uX, uv - vec2(0.0, texel)).xyz;
  delta = neighbor - x; d = length(delta);
  dx += delta * (d - uRestLen) / max(d, 1e-5) * 0.25 * uStiffness;

  neighbor = texture(uX, uv + vec2(0.0, texel)).xyz;
  delta = neighbor - x; d = length(delta);
  dx += delta * (d - uRestLen) / max(d, 1e-5) * 0.25 * uStiffness;

  x += dx;

  // Floor constraint.
  if (x.y < uFloor) x.y = uFloor;

  // Sphere constraint.
  vec3 sDelta = x - uSpherePos;
  float sDist = length(sDelta);
  if (sDist < uSphereRadius) {
    x = uSpherePos + sDelta * (uSphereRadius / max(sDist, 1e-5));
  }

  outColor = vec4(x, 1.0);
}`;

// --- Init pass: fill the position texture with the starting state. ---
const FRAG_INIT = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;
uniform float uGridSize;
void main() {
  // Cloth hangs from y=4 (top) to y=0 (bottom). Width spans x=-5..+5.
  float row = vUV.y * (uGridSize - 1.0);
  float col = vUV.x * (uGridSize - 1.0);
  // Top row pinned at (0, 4, 0). Other rows hang with slight inward
  // curve at the bottom (gravity would do this).
  float pinTop = step(row, 0.5);
  float x = (col - (uGridSize - 1.0) * 0.5) * (10.0 / (uGridSize - 1.0));
  float curve = pow(row / max(uGridSize - 1.0, 1.0), 1.5) * 0.4;
  x *= (1.0 - curve);
  x = mix(x, 0.0, pinTop);
  float y = mix(0.0, 4.0, 1.0 - row / max(uGridSize - 1.0, 1.0));
  y = mix(y, 4.0, pinTop);
  outColor = vec4(x, y, 0.0, 1.0);
}`;

// --- 3. Render pass: read positions, compute normals, light, output. ---
const FRAG_RENDER = `#version 300 es
precision highp float;
in vec3 vWorldPos;
in vec2 vUV;
out vec4 outColor;
uniform sampler2D uX;
uniform float uTime;
uniform vec3 uLight1Pos;
uniform vec3 uLight1Color;
uniform vec3 uLight2Pos;
uniform vec3 uLight2Color;
uniform vec3 uCameraPos;

void main() {
  // Compute the normal from partial differences in screen space using
  // dFdx/dFdy. This gives the normal of the underlying surface based
  // on the local triangle's orientation in the rendered mesh.
  vec3 dPdx = dFdx(vWorldPos);
  vec3 dPdy = dFdy(vWorldPos);
  vec3 n = normalize(cross(dPdx, dPdy));
  if (n.z < 0.0) n = -n;  // make sure it faces the camera (+Z)

  // Iridescent color that shifts across the cloth + time.
  float hue = 0.55 + 0.25 * sin(uTime * 0.2 + vUV.x * 4.0) + 0.1 * vUV.y;
  float r = 0.5 + 0.5 * cos(6.2831853 * (hue + 0.00));
  float g = 0.5 + 0.5 * cos(6.2831853 * (hue + 0.33));
  float b = 0.5 + 0.5 * cos(6.2831853 * (hue + 0.66));
  vec3 base = vec3(r, g, b) * 0.6 + vec3(0.3, 0.2, 0.35);

  // Two-light Blinn-Phong with rim.
  vec3 v = normalize(uCameraPos - vWorldPos);
  vec3 l1 = normalize(uLight1Pos - vWorldPos);
  vec3 l2 = normalize(uLight2Pos - vWorldPos);

  float diff1 = max(dot(n, l1), 0.0);
  float diff2 = max(dot(n, l2), 0.0);
  float NdotV = max(dot(n, v), 0.0);
  float spec1 = pow(max(dot(reflect(-l1, n), -v), 0.0), 64.0);
  float spec2 = pow(max(dot(reflect(-l2, n), -v), 0.0), 64.0);

  vec3 lit = base * (uLight1Color * diff1 * 0.9 + uLight2Color * diff2 * 0.7);
  lit += uLight1Color * spec1 * 0.7 + uLight2Color * spec2 * 0.5;
  // Rim light (thin-film glow at silhouette edges).
  lit += base * pow(1.0 - NdotV, 3.0) * 0.8;

  // Atmospheric fog.
  float dist = length(uCameraPos - vWorldPos);
  float fog = 1.0 - exp(-max(dist - 5.0, 0.0) * 0.15);
  vec3 bg = vec3(0.04, 0.03, 0.08);
  lit = mix(lit, bg, clamp(fog, 0.0, 0.6));

  outColor = vec4(lit, 1.0);
}
`;

// Fullscreen quad for the simulation passes.
const QUAD_VERTS = new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]);

/**
 * Build the cloth mesh: GRID×GRID vertices, each with a vUV that
 * maps to a particle position in the position texture. Indexed
 * triangle list with (GRID-1)² × 2 triangles = 18050 triangles.
 */
function buildClothMesh(gridSize: number): {
  vertices: Float32Array;
  indices: Uint16Array;
} {
  const verts = new Float32Array(gridSize * gridSize * 2);
  let vi = 0;
  for (let row = 0; row < gridSize; row++) {
    for (let col = 0; col < gridSize; col++) {
      // vUV: col/gridSize..(col+1)/gridSize, row/gridSize..(row+1)/gridSize
      // Use texel center (add 0.5 / texres) for sampling to hit the
      // right particle. With texres=gridSize+2, the particle at row,col
      // is at (col+1, row+1) in the texture (1-pixel halo).
      const u = (col + 0.5) / (gridSize + 2 - 1);
      const v = (row + 0.5) / (gridSize + 2 - 1);
      // Actually, simpler: vUV = (col+0.5)/gridSize, (row+0.5)/gridSize.
      // Then add 1/texres to skip the halo. Cleaner: use the same
      // indexing as the integrate/constrain passes, which use
      // vUV = (col, row) / (gridSize-1) for col,row in [0, gridSize-1].
      // We want vUV = (col+0.5) / (gridSize-1+1) = (col+0.5) / gridSize
      // which is in [0.5/gridSize, 1 - 0.5/gridSize].
      const uu = (col + 0.5) / gridSize;
      const vv = (row + 0.5) / gridSize;
      verts[vi++] = uu;
      verts[vi++] = vv;
    }
  }

  const indices = new Uint16Array((gridSize - 1) * (gridSize - 1) * 6);
  let ii = 0;
  for (let row = 0; row < gridSize - 1; row++) {
    for (let col = 0; col < gridSize - 1; col++) {
      const tl = row * gridSize + col;
      const tr = tl + 1;
      const bl = (row + 1) * gridSize + col;
      const br = bl + 1;
      indices[ii++] = tl;
      indices[ii++] = bl;
      indices[ii++] = tr;
      indices[ii++] = tr;
      indices[ii++] = bl;
      indices[ii++] = br;
    }
  }

  return { vertices: verts, indices };
}

function makePosFBO(gl: WebGL2RenderingContext): { fbo: WebGLFramebuffer; tex: WebGLTexture } | null {
  const tex = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA16F, TEXRES, TEXRES, 0, gl.RGBA, gl.HALF_FLOAT, null);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

  const fbo = gl.createFramebuffer();
  gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
  const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
  if (status !== gl.FRAMEBUFFER_COMPLETE) {
    console.error("[cloth] FBO incomplete: 0x" + status.toString(16));
    return null;
  }
  return { fbo, tex };
}

function makeFullScreenPipeline(
  gl: WebGL2RenderingContext,
  fragSrc: string,
  vertSrc: string,
  uniforms: string[],
): { prog: WebGLProgram; vao: WebGLVertexArrayObject; vbo: WebGLBuffer; locs: Record<string, WebGLUniformLocation | null> } | null {
  const prog = linkProgram(gl, vertSrc, fragSrc, POST_ATTRIBS);
  if (!prog) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, QUAD_VERTS, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  const locs: Record<string, WebGLUniformLocation | null> = {};
  for (const u of uniforms) {
    locs[u] = gl.getUniformLocation(prog, u);
  }
  return { prog, vao, vbo, locs };
}

function makeClothMeshPipeline(
  gl: WebGL2RenderingContext,
  vertSrc: string,
  fragSrc: string,
  uniforms: string[],
  mesh: { vertices: Float32Array; indices: Uint16Array },
): { prog: WebGLProgram; vao: WebGLVertexArrayObject; vbo: WebGLBuffer; ibo: WebGLBuffer; locs: Record<string, WebGLUniformLocation | null> } | null {
  const prog = linkProgram(gl, vertSrc, fragSrc, []);
  if (!prog) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  const ibo = gl.createBuffer();
  if (!vao || !vbo || !ibo) return null;
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, mesh.vertices, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
  gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, mesh.indices, gl.STATIC_DRAW);
  gl.bindVertexArray(null);
  const locs: Record<string, WebGLUniformLocation | null> = {};
  for (const u of uniforms) {
    locs[u] = gl.getUniformLocation(prog, u);
  }
  return { prog, vao, vbo, ibo, locs };
}

export function createClothPipeline(gl: WebGL2RenderingContext): ClothPipeline | null {
  if (!gl.getExtension("EXT_color_buffer_float")) {
    console.error("[cloth] EXT_color_buffer_float not available");
    return null;
  }

  const integrate = makeFullScreenPipeline(gl, FRAG_INTEGRATE, VERT_QUAD, [
    "uX", "uOX", "uTimestep", "uDamping", "uWind", "uGravity", "uGridSize",
  ]);
  if (!integrate) return null;
  const constrain = makeFullScreenPipeline(gl, FRAG_CONSTRAIN, VERT_QUAD, [
    "uX", "uGridSize", "uRestLen", "uStiffness", "uFloor", "uSpherePos", "uSphereRadius",
  ]);
  if (!constrain) return null;
  const init = makeFullScreenPipeline(gl, FRAG_INIT, VERT_QUAD, ["uGridSize"]);
  if (!init) return null;

  const mesh = buildClothMesh(GRID);
  const render = makeClothMeshPipeline(
    gl,
    VERT_CLOTH,
    FRAG_RENDER,
    ["uX", "uTime", "uCameraPos", "uLight1Pos", "uLight1Color", "uLight2Pos", "uLight2Color"],
    mesh,
  );
  if (!render) return null;

  const a = makePosFBO(gl);
  const b = makePosFBO(gl);
  if (!a || !b) return null;

  return {
    programs: {
      integrate: integrate.prog,
      constrain: constrain.prog,
      init: init.prog,
      render: render.prog,
    },
    vaos: {
      integrate: integrate.vao,
      constrain: constrain.vao,
      init: init.vao,
      render: render.vao,
    },
    vbos: {
      integrate: integrate.vbo,
      constrain: constrain.vbo,
      init: init.vbo,
      render: render.vbo,
      renderIndices: render.ibo,
    },
    uniforms: {
      integrate: {
        xTex: integrate.locs.uX,
        oxTex: integrate.locs.uOX,
        timestep: integrate.locs.uTimestep,
        damping: integrate.locs.uDamping,
        wind: integrate.locs.uWind,
        gravity: integrate.locs.uGravity,
        gridSize: integrate.locs.uGridSize,
      },
      constrain: {
        xTex: constrain.locs.uX,
        gridSize: constrain.locs.uGridSize,
        restLen: constrain.locs.uRestLen,
        stiffness: constrain.locs.uStiffness,
        floor: constrain.locs.uFloor,
        spherePos: constrain.locs.uSpherePos,
        sphereRadius: constrain.locs.uSphereRadius,
      },
      init: {
        gridSize: init.locs.uGridSize,
      },
      render: {
        xTex: render.locs.uX,
        time: render.locs.uTime,
        cameraPos: render.locs.uCameraPos,
        light1Pos: render.locs.uLight1Pos,
        light1Color: render.locs.uLight1Color,
        light2Pos: render.locs.uLight2Pos,
        light2Color: render.locs.uLight2Color,
      },
    },
    fboA: a.fbo,
    texA: a.tex,
    fboB: b.fbo,
    texB: b.tex,
    gridSize: GRID,
    texRes: TEXRES,
    fboCurrent: a.fbo,
    texCurrent: a.tex,
    fboPrev: b.fbo,
    texPrev: b.tex,
    vertexCount: mesh.vertices.length / 2,
    indexCount: mesh.indices.length,
  };
}

export function initCloth(gl: WebGL2RenderingContext, pipe: ClothPipeline): void {
  for (const fbo of [pipe.fboA, pipe.fboB]) {
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.viewport(0, 0, TEXRES, TEXRES);
    gl.useProgram(pipe.programs.init);
    gl.bindVertexArray(pipe.vaos.init);
    gl.uniform1f(pipe.uniforms.init.gridSize, GRID);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.bindVertexArray(null);
}

export function stepCloth(
  gl: WebGL2RenderingContext,
  pipe: ClothPipeline,
  width: number,
  height: number,
  t: number,
): void {
  // === 1. Integrate: read fboCurrent, write new positions to fboB. ===
  gl.bindFramebuffer(gl.FRAMEBUFFER, pipe.fboB);
  gl.viewport(0, 0, TEXRES, TEXRES);
  gl.useProgram(pipe.programs.integrate);
  gl.bindVertexArray(pipe.vaos.integrate);

  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, pipe.texCurrent);
  gl.uniform1i(pipe.uniforms.integrate.xTex, 0);
  gl.activeTexture(gl.TEXTURE1);
  gl.bindTexture(gl.TEXTURE_2D, pipe.texPrev);
  gl.uniform1i(pipe.uniforms.integrate.oxTex, 1);

  gl.uniform1f(pipe.uniforms.integrate.timestep, 1.0 / 60.0);
  gl.uniform1f(pipe.uniforms.integrate.damping, 0.985);
  // Wind: gentle breeze with two sine components.
  gl.uniform3f(
    pipe.uniforms.integrate.wind,
    Math.sin(t * 0.7) * 1.5 + Math.sin(t * 2.3) * 0.8,
    0.0,
    Math.cos(t * 1.1) * 1.2,
  );
  gl.uniform3f(pipe.uniforms.integrate.gravity, 0.0, -9.8, 0.0);
  gl.uniform1f(pipe.uniforms.integrate.gridSize, GRID);

  gl.drawArrays(gl.TRIANGLES, 0, 6);

  // === 2. Constrain — 6 iterations for stiffness. ===
  for (let i = 0; i < 6; i++) {
    gl.bindFramebuffer(gl.FRAMEBUFFER, pipe.fboB);
    gl.viewport(0, 0, TEXRES, TEXRES);
    gl.useProgram(pipe.programs.constrain);
    gl.bindVertexArray(pipe.vaos.constrain);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, pipe.texB);
    gl.uniform1i(pipe.uniforms.constrain.xTex, 0);

    gl.uniform1f(pipe.uniforms.constrain.gridSize, GRID);
    gl.uniform1f(pipe.uniforms.constrain.restLen, 10.0 / (GRID - 1) * 1.0);
    gl.uniform1f(pipe.uniforms.constrain.stiffness, 0.5);
    gl.uniform1f(pipe.uniforms.constrain.floor, -2.0);
    const sphereT = t * 0.4;
    const sphereX = Math.sin(sphereT) * 1.2;
    const sphereZ = Math.cos(sphereT * 0.7) * 0.8;
    const sphereY = 0.5 + Math.sin(sphereT * 1.3) * 0.4;
    gl.uniform3f(pipe.uniforms.constrain.spherePos, sphereX, sphereY, sphereZ);
    gl.uniform1f(pipe.uniforms.constrain.sphereRadius, 1.0);

    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }

  // === 3. Swap. ===
  const oldCurrent = pipe.fboCurrent;
  const oldTexCurrent = pipe.texCurrent;
  pipe.fboCurrent = pipe.fboB;
  pipe.texCurrent = pipe.texB;
  pipe.fboPrev = oldCurrent;
  pipe.texPrev = oldTexCurrent;

  // === 4. Render the cloth mesh to the default framebuffer. ===
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.viewport(0, 0, width, height);
  gl.useProgram(pipe.programs.render);
  gl.bindVertexArray(pipe.vaos.render);
  gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, pipe.vbos.renderIndices);

  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, pipe.texCurrent);
  gl.uniform1i(pipe.uniforms.render.xTex, 0);

  gl.uniform1f(pipe.uniforms.render.time, t);

  // Camera: orbit slowly.
  const camT = t * 0.2;
  const camR = 5.0 + 0.5 * Math.sin(camT * 0.3);
  gl.uniform3f(
    pipe.uniforms.render.cameraPos,
    Math.cos(camT) * camR,
    0.5 + Math.sin(camT * 0.7) * 0.5,
    Math.sin(camT) * camR,
  );

  // Two lights orbiting the cloth.
  gl.uniform3f(pipe.uniforms.render.light1Pos,
    Math.cos(t * 0.6) * 5.0, 4.0, Math.sin(t * 0.6) * 5.0);
  gl.uniform3f(pipe.uniforms.render.light1Color, 1.0, 0.7, 0.4);
  gl.uniform3f(pipe.uniforms.render.light2Pos,
    Math.cos(-t * 0.5) * 4.0, 2.0, Math.sin(-t * 0.5) * 4.0);
  gl.uniform3f(pipe.uniforms.render.light2Color, 0.3, 0.6, 1.0);

  gl.drawElements(gl.TRIANGLES, pipe.indexCount, gl.UNSIGNED_SHORT, 0);
  gl.bindVertexArray(null);
  gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, null);
}
